import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
} from "react";
import { apiService } from "../services/api";
import { User } from "../types";

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string) => Promise<void>;
    logout: () => void;
    /** Show changes to the signed-in user (e.g. a new display name) at once. */
    updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Check if user is already authenticated on app start
        const checkAuth = async () => {
            try {
                if (apiService.isAuthenticated()) {
                    // Fetch real user info from API
                    const userData = await apiService.getCurrentUser();
                    setUser(userData);
                }
            } catch (error) {
                console.error("Auth check failed:", error);
                apiService.logout();
            } finally {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, []);

    const login = async (email: string, password: string): Promise<void> => {
        try {
            setIsLoading(true);
            await apiService.login(email, password);

            // Fetch real user info after successful login
            const userData = await apiService.getCurrentUser();
            setUser(userData);
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (email: string, password: string): Promise<void> => {
        try {
            setIsLoading(true);
            await apiService.register(email, password);
            // Don't auto-login, let user manually login after registration
        } catch (error) {
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = (): void => {
        apiService.logout();
        setUser(null);
    };

    const value: AuthContextType = {
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        updateUser: setUser,
    };

    return (
        <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
